import type { routes } from '@semios/app-platform-banyan-route-definitions'
import { translate } from 'i18n/i18n'
import type { TProperty, TPropertyId } from 'stores/fieldAssetStore/helperTypes'
import { searchStringFromStringWithSpaces } from 'utils/searchStringFromStringWithSpaces'
import { sortByKey } from 'utils/sortByKey'
import type { TSearchResultsGroup, TSearchResultsItem, TSearchTypes } from '../types'
import { administrativeAreaLevel1T } from './administrativeAreaLevel1T'
import { countryT } from './countryT'
import { generatePropertyLabel } from './generatePropertyLabel'

const MAX_PROPERTIES_FOR_EMPTY_SEARCH_STATE = 50

const labelWithCount = (label: string, count: number) =>
  translate.phrases.templates('{{label}} ({{count}})', { label, count })

const getItemsGroupedByLabel = (
  type: TSearchTypes,
  propertiesSortedByName: TProperty[],
  getLabel: (property: TProperty) => string,
): TSearchResultsItem[] => {
  const propertyIdsByLabel: Record<string, TPropertyId[]> = {}

  propertiesSortedByName.forEach((property) => {
    const label = getLabel(property)

    propertyIdsByLabel[label] = propertyIdsByLabel[label] ?? []

    propertyIdsByLabel[label].push(property.propertyId)
  })

  return Object.entries(propertyIdsByLabel)
    .map(([label, propertyIds]) => ({ type, label, propertyIds, value: `${type}-${label}` }))
    .sort(sortByKey('label'))
}

export const getSearchResults = ({
  properties,
  previousSearches,
  searchText,
}: {
  properties: NonNullable<routes.UserAppStartup.Response['properties']>
  previousSearches: TSearchResultsItem[]
  searchText: string
}): TSearchResultsGroup[] => {
  const propertiesSortedByName = Object.values(properties).sort(sortByKey('propertyName'))

  const propertyItems: TSearchResultsItem[] = propertiesSortedByName.map(({ propertyId, propertyName }) => ({
    type: 'PROPERTY',
    label: generatePropertyLabel({ propertyId, propertyName }),
    propertyIds: [propertyId],
    value: `PROPERTY-${propertyId}`,
  }))

  const previousSearchesGroup = {
    group: translate.phrases.banyanApp('Recently Searched'),
    // entries saved by older releases also hold a `group` key, which Mantine reads as a group with no `items`
    items: previousSearches.map(({ type, label, propertyIds, value }) => ({
      type,
      label,
      propertyIds,
      value,
    })),
  }

  if (!searchText) {
    return [
      previousSearchesGroup,
      {
        group: labelWithCount(translate.phrases.banyanApp('Property'), propertyItems.length),
        items: propertyItems.slice(0, MAX_PROPERTIES_FOR_EMPTY_SEARCH_STATE),
      },
    ].filter(({ items }) => items.length)
  }

  const matchingGroups = [
    { label: translate.phrases.banyanApp('Property'), items: propertyItems },
    {
      label: translate.phrases.banyanApp('Organization'),
      items: getItemsGroupedByLabel('ORGANIZATION', propertiesSortedByName, (p) => p.organizationName),
    },
    {
      label: translate.phrases.banyanApp('State/Province'),
      items: getItemsGroupedByLabel('ADMINISTRATIVE_AREA_LEVEL_1', propertiesSortedByName, (p) =>
        administrativeAreaLevel1T({
          country: p.country,
          gAdministrativeAreaLevel1: p.gAdministrativeAreaLevel1 ?? '',
        }),
      ),
    },
    {
      label: translate.phrases.banyanApp('Country'),
      items: getItemsGroupedByLabel('COUNTRY', propertiesSortedByName, (p) => countryT(p.country)),
    },
  ]
    .map(({ label, items }) => ({
      label,
      items: items.filter((item) => searchStringFromStringWithSpaces(searchText, item.label)),
    }))
    .filter(({ items }) => items.length)

  if (!matchingGroups.length) return []

  const maxItemsPerGroup = matchingGroups.length > 1 ? 3 : 10

  return [
    ...matchingGroups.map(({ label, items }) => ({
      group: labelWithCount(label, items.length),
      items: items.slice(0, maxItemsPerGroup),
    })),
    previousSearchesGroup,
  ].filter(({ items }) => items.length)
}
